import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { perMachineSummarySalesData, perMachineTotalSalesData } from '@dataTypes/perMachineTypes';
import { PERMACHINE } from './queryKey/queryKey';
import { deliverServiceCoinBySocket } from '@apis/perMachine';
import { AxiosHeaders } from 'axios';
import { useSetRecoilState } from 'recoil';
import errorOrYesModalState from '../../recoil/errorOrYesModal/atom';
import { imgTypes } from '@dataTypes/imgTypes';
import { Minus, Plus } from 'lucide-react';

const MAX_MULTIPLIER = 10;

export type ServiceCoinMachine = perMachineTotalSalesData | perMachineSummarySalesData;

export interface TerminalC0Info {
  amount?: string | null;
  pulse?: string | null;
}

function parseBaseAmount(amountStr: string | null | undefined): number | null {
  if (amountStr == null || String(amountStr).trim() === '') return null;
  const n = parseInt(String(amountStr).trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function machineLabel(m: ServiceCoinMachine): string {
  return (m.deviceAlias?.trim() || m.catId?.trim() || '').trim() || m.catId;
}

interface ServiceCoinModalProps {
  show: boolean;
  handleClose: () => void;
  /** 1대 이상 */
  machines: ServiceCoinMachine[];
  sameDate: boolean;
  businessNum: string | undefined;
  /** 단말 catId별 C0 설정 금액(웹소켓/서버 기록) */
  getTerminalC0: (catId: string) => TerminalC0Info | undefined;
}

const ServiceCoinModal = ({
  show,
  handleClose,
  machines,
  sameDate,
  businessNum,
  getTerminalC0,
}: ServiceCoinModalProps) => {
  const queryClient = useQueryClient();
  const [multiplier, setMultiplier] = useState(1);
  const setErrorState = useSetRecoilState(errorOrYesModalState);
  const [isDelivering, setIsDelivering] = useState(false);
  const [deliverProgress, setDeliverProgress] = useState<{ done: number; total: number } | null>(
    null,
  );

  const primary = machines[0];
  const isBulk = machines.length > 1;

  const rows = useMemo(() => {
    return machines.map((m) => {
      const c0 = getTerminalC0(m.catId.trim());
      const base = parseBaseAmount(c0?.amount);
      return { machine: m, base, deliver: base != null ? base * multiplier : 0 };
    });
  }, [machines, getTerminalC0, multiplier]);

  const missingBaseLabels = useMemo(
    () => rows.filter((r) => r.base == null).map((r) => machineLabel(r.machine)),
    [rows],
  );

  const basesPositive = rows.every((r) => r.base != null);
  const minDeliver = useMemo(() => {
    const ds = rows.map((r) => r.deliver).filter((d) => d > 0);
    if (!ds.length) return 0;
    return Math.min(...ds);
  }, [rows]);
  const maxDeliver = useMemo(() => {
    const ds = rows.map((r) => r.deliver).filter((d) => d > 0);
    if (!ds.length) return 0;
    return Math.max(...ds);
  }, [rows]);

  useEffect(() => {
    if (!show) setMultiplier(1);
  }, [show]);

  const handleSubmitSocket = async () => {
    if (!machines.length || !businessNum) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '오류가 발생했습니다.',
        text: '대상 단말 또는 사업자 번호 정보가 없습니다.',
        modalBG: true,
      });
      return;
    }
    if (!basesPositive) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '입력 오류',
        text:
          missingBaseLabels.length > 0
            ? `단말기 설정 금액이 없는 단말이 있습니다: ${missingBaseLabels.slice(0, 8).join(', ')}${missingBaseLabels.length > 8 ? ` 외 ${missingBaseLabels.length - 8}대` : ''}`
            : '단말기 설정 금액을 불러올 수 없습니다.',
        modalBG: true,
      });
      return;
    }

    setIsDelivering(true);
    setDeliverProgress({ done: 0, total: machines.length });
    const failures: string[] = [];

    for (let i = 0; i < rows.length; i++) {
      const { machine, base, deliver } = rows[i];
      const catId = machine.catId?.trim() ?? '';
      const label = machineLabel(machine);
      if (base == null || deliver <= 0) {
        failures.push(`${label}: 설정 금액 없음`);
        setDeliverProgress({ done: i + 1, total: machines.length });
        continue;
      }
      try {
        const data = await deliverServiceCoinBySocket(catId, businessNum, deliver);
        if (!data.success) {
          failures.push(`${label}: ${data.message ?? '서비스 금액 지급에 실패했습니다.'}`);
        }
      } catch (error: unknown) {
        const ax = error as AxiosHeaders & { response?: { data?: { message?: string } } };
        failures.push(
          `${label}: ${ax.response?.data?.message ?? 'WebSocket 지급에 실패했습니다.'}`,
        );
      }
      setDeliverProgress({ done: i + 1, total: machines.length });
    }

    setIsDelivering(false);
    setDeliverProgress(null);

    const okCount = machines.length - failures.length;
    if (sameDate) {
      queryClient.invalidateQueries([PERMACHINE.PERMACHINESALESLISTS]);
    } else {
      queryClient.invalidateQueries([PERMACHINE.PERMACHINESUMMARYSALESLISTS]);
    }

    if (failures.length === machines.length) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '지급 실패',
        text:
          failures.slice(0, 12).join('\n') +
          (failures.length > 12 ? `\n… 외 ${failures.length - 12}건` : ''),
        modalBG: true,
      });
      return;
    }

    if (failures.length > 0) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '일부 단말 지급 실패',
        text: `${failures.length}대 실패, ${okCount}대 성공.\n${failures.slice(0, 10).join('\n')}${failures.length > 10 ? `\n… 외 ${failures.length - 10}건` : ''}`,
        modalBG: true,
      });
    }

    setMultiplier(1);
    if (failures.length === 0) {
      handleClose();
    }
  };

  const closeModalWithReset = () => {
    setMultiplier(1);
    handleClose();
  };

  const minusDisabled = multiplier <= 1;
  const plusDisabled = multiplier >= MAX_MULTIPLIER;

  if (!show || !primary) {
    return null;
  }

  const primaryC0 = getTerminalC0(primary.catId.trim());
  const primaryBase = parseBaseAmount(primaryC0?.amount);

  return (
    <Modal
      id="modify_service_coin_modal_wrapper"
      show={show}
      onHide={isDelivering ? () => undefined : closeModalWithReset}
      centered
      backdrop={isDelivering ? 'static' : true}
    >
      <Modal.Header className="modify_modal_header" closeButton={!isDelivering}>
        <Modal.Title className="service_coin_header_title">
          서비스 금액 지급{isBulk ? ` (일괄 ${machines.length}대)` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="modify_detail_body">
        <div className="service_coin_modal_wrapper">
          <div className="service_coin_title">
            {isBulk ? (
              <>
                <span className="device_alias">
                  선택 {machines.length}대 — 단말마다 설정 금액 × 동일 배수로 지급됩니다.
                </span>
                <span className="current_coin text-muted small d-block mt-1">
                  {minDeliver > 0 &&
                    maxDeliver > 0 &&
                    (minDeliver !== maxDeliver
                      ? `이번 배수 기준 지급액: ${minDeliver.toLocaleString('ko-KR')}원 ~ ${maxDeliver.toLocaleString('ko-KR')}원`
                      : `이번 배수 기준 지급액: 각 ${maxDeliver.toLocaleString('ko-KR')}원`)}
                </span>
              </>
            ) : (
              <>
                <span className="device_alias">{primary.deviceAlias}</span>
                <span className="current_coin">
                  현재 금액: {primary.coin.toLocaleString('ko-KR')}원
                </span>
              </>
            )}
          </div>

          <div className="service_amount_c0_info mb-3" style={{ textAlign: 'left', width: '100%' }}>
            <div className="d-flex flex-wrap gap-3">
              {!isBulk && (
                <span>
                  <strong>단말기 설정 금액</strong>:{' '}
                  {primaryBase != null ? `${primaryBase.toLocaleString('ko-KR')}원` : '-'}
                </span>
              )}
            </div>
            {!isBulk && primaryBase == null && (
              <p className="small text-danger mt-2 mb-0">
                단말기 설정 금액이 없으면 배수 지급을 사용할 수 없습니다.
              </p>
            )}
            {isBulk && missingBaseLabels.length > 0 && (
              <p className="small text-danger mt-2 mb-0">
                설정 금액 없음 (지급 불가): {missingBaseLabels.slice(0, 6).join(', ')}
                {missingBaseLabels.length > 6 ? ` 외 ${missingBaseLabels.length - 6}대` : ''}
              </p>
            )}
          </div>

          <div className="service_coin_title mb-2">
            <span className="current_coin" style={{ fontSize: '1.05rem' }}>
              {isBulk ? (
                <>
                  배수: <strong>{multiplier}</strong>
                  <span className="text-muted ms-2">(최대 {MAX_MULTIPLIER}배)</span>
                </>
              ) : (
                <>
                  지급 금액:{' '}
                  <strong>
                    {primaryBase != null
                      ? `${(primaryBase * multiplier).toLocaleString('ko-KR')}원`
                      : '-'}
                  </strong>
                  {primaryBase != null && (
                    <span className="text-muted ms-2">
                      (기준 {primaryBase.toLocaleString('ko-KR')}원 × {multiplier}배, 최대{' '}
                      {MAX_MULTIPLIER}배)
                    </span>
                  )}
                </>
              )}
            </span>
          </div>

          <div className="service_coin_input">
            <Button
              className="service_coin_button"
              disabled={minusDisabled || isDelivering}
              onClick={() => setMultiplier((m) => Math.max(1, m - 1))}
            >
              <Minus size={28} />
            </Button>
            <div className="service_coin_input_text d-flex align-items-center justify-content-center px-2">
              {multiplier}
            </div>
            <Button
              className="service_coin_button"
              disabled={plusDisabled || isDelivering}
              onClick={() => setMultiplier((m) => Math.min(MAX_MULTIPLIER, m + 1))}
            >
              <Plus size={28} />
            </Button>
          </div>

          <span className="caution_text">
            서비스 금액은 해당 단말기에 설정된 기본금액 배수로 지급 가능합니다 (최대{' '}
            {MAX_MULTIPLIER}배).
          </span>
        </div>

        <div className="button_wrapper">
          <Button
            className="submit_btn socket_btn"
            onClick={() => void handleSubmitSocket()}
            disabled={
              !basesPositive || !businessNum || isDelivering || rows.some((r) => r.deliver <= 0)
            }
            title={
              !businessNum
                ? '사업자 번호가 필요합니다. (store 응답에 businessNum 포함 필요)'
                : undefined
            }
          >
            {isDelivering && deliverProgress
              ? `지급 중… ${deliverProgress.done}/${deliverProgress.total}`
              : isDelivering
                ? '처리 중...'
                : '서비스 금액 지급'}
          </Button>
          <Button className="cancel_btn" onClick={closeModalWithReset} disabled={isDelivering}>
            취소
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ServiceCoinModal;
